// Guarda o estado transitório de AjusteGrade.js que hoje se perde ao trocar
// de menu: a lista de alunos selecionados manualmente, as edições de grade
// ainda não salvas, e todos os filtros (os de cima, os por coluna, os de
// área e a busca por inscrito). Como o Provider fica acima do <Router> em
// App.js, ele sobrevive à troca de rota (o componente AjusteGrade é
// desmontado, mas esse estado não mora nele) — sem isso, sair pra outro menu
// e voltar perdia os filtros que o usuário tinha montado.
//
// Filtros e busca (não as edições pendentes) também são salvos em
// localStorage, isolados por instituição — sobrevivem a um F5 ou fechar a
// aba, e só são zerados de verdade quando o usuário clica "Limpar filtros"
// na tela (ver limparFiltros abaixo) ou troca de instituição. `alteracoes`
// fica de fora de propósito (é estado de trabalho em andamento — edições
// ainda não salvas — trazer de volta uma edição esquecida depois de dias
// seria mais perigoso que útil), mas `alunosSelecionados` (a lista montada
// à mão pela busca por inscrito) É zerada por "Limpar filtros" — pedido
// explícito do usuário, já que deixar essa lista sobreviver fazia a grade
// continuar ignorando os filtros de cima mesmo depois de "limpos".
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { AREAS } from '../../utils/areas';

const AjusteGradeContext = createContext(null);

const areasIniciais = () => Object.fromEntries(AREAS.map((a) => [a.value, true]));

const CHAVE_PERSISTENCIA = 'ajusteGrade:filtros';

const estadoFiltrosPadrao = () => ({
  atividadeFiltro: '',
  professorFiltro: '',
  diaFiltro: '',
  horarioFiltro: '',
  turnoFiltro: '',
  filtrosColuna: {},
  areasAtivas: areasIniciais(),
  buscaAluno: '',
});

const lerFiltrosPersistidos = (institutionId) => {
  const padrao = estadoFiltrosPadrao();
  if (!institutionId) return padrao;
  try {
    const salvo = localStorage.getItem(`${CHAVE_PERSISTENCIA}:${institutionId}`);
    return salvo ? { ...padrao, ...JSON.parse(salvo) } : padrao;
  } catch {
    return padrao;
  }
};

export function AjusteGradeProvider({ children }) {
  const { institutionId } = useInstitution();
  const [alunosSelecionados, setAlunosSelecionados] = useState([]);
  const [alteracoes, setAlteracoes] = useState({});
  const [atividadeFiltro, setAtividadeFiltro] = useState('');
  const [professorFiltro, setProfessorFiltro] = useState('');
  const [diaFiltro, setDiaFiltro] = useState('');
  const [horarioFiltro, setHorarioFiltro] = useState('');
  const [turnoFiltro, setTurnoFiltro] = useState('');
  const [filtrosColuna, setFiltrosColuna] = useState({});
  const [areasAtivas, setAreasAtivas] = useState(areasIniciais);
  const [buscaAluno, setBuscaAluno] = useState('');

  // Fica true só depois que os filtros persistidos da instituição atual já
  // foram carregados pro state — é um STATE (não uma ref) de propósito: uma
  // ref viraria true de forma síncrona ainda dentro do próprio efeito de
  // leitura, antes das chamadas setXxx() abaixo serem de fato aplicadas, e o
  // efeito de salvar (que roda na mesma leva) acabaria disparando com o
  // state ANTIGO (vazio) mesmo já "liberado" pela ref — sobrescrevendo o que
  // acabou de ser lido. Usando state, o "liberar" só fica visível pro efeito
  // de salvar quando roda no mesmo render em que atividadeFiltro/diaFiltro/
  // etc. JÁ são os valores recém-carregados (mesmo lote de atualização).
  const [carregouFiltros, setCarregouFiltros] = useState(false);

  useEffect(() => {
    setCarregouFiltros(false);
    setAlunosSelecionados([]);
    setAlteracoes({});
    const filtros = lerFiltrosPersistidos(institutionId);
    setAtividadeFiltro(filtros.atividadeFiltro);
    setProfessorFiltro(filtros.professorFiltro);
    setDiaFiltro(filtros.diaFiltro);
    setHorarioFiltro(filtros.horarioFiltro);
    setTurnoFiltro(filtros.turnoFiltro);
    setFiltrosColuna(filtros.filtrosColuna);
    setAreasAtivas(filtros.areasAtivas);
    setBuscaAluno(filtros.buscaAluno);
    setCarregouFiltros(true);
  }, [institutionId]);

  useEffect(() => {
    if (!institutionId || !carregouFiltros) return;
    const estado = {
      atividadeFiltro,
      professorFiltro,
      diaFiltro,
      horarioFiltro,
      turnoFiltro,
      filtrosColuna,
      areasAtivas,
      buscaAluno,
    };
    localStorage.setItem(`${CHAVE_PERSISTENCIA}:${institutionId}`, JSON.stringify(estado));
  }, [
    institutionId,
    carregouFiltros,
    atividadeFiltro,
    professorFiltro,
    diaFiltro,
    horarioFiltro,
    turnoFiltro,
    filtrosColuna,
    areasAtivas,
    buscaAluno,
  ]);

  // Única forma de zerar os filtros persistidos (botão "Limpar filtros" da
  // tela) — troca de instituição já reseta por conta própria (efeito acima).
  // Também zera `alunosSelecionados` (a lista montada à mão pela busca por
  // inscrito): pedido explícito do usuário — diferente de `alteracoes`
  // (edições pendentes ainda não salvas), que continua de fora de propósito,
  // "Limpar filtros" deve mesmo descartar essa lista manual, já que ela é o
  // que faz a grade ignorar todos os filtros de cima (ver alunosFiltrados em
  // AjusteGrade.js).
  const limparFiltros = () => {
    const padrao = estadoFiltrosPadrao();
    setAtividadeFiltro(padrao.atividadeFiltro);
    setProfessorFiltro(padrao.professorFiltro);
    setDiaFiltro(padrao.diaFiltro);
    setHorarioFiltro(padrao.horarioFiltro);
    setTurnoFiltro(padrao.turnoFiltro);
    setFiltrosColuna(padrao.filtrosColuna);
    setAreasAtivas(padrao.areasAtivas);
    setBuscaAluno(padrao.buscaAluno);
    setAlunosSelecionados([]);
  };

  // Memoizado: sem isso, o Provider cria um objeto `value` novo a cada
  // render (mesmo quando nenhum desses campos mudou de verdade), e como ele
  // fica acima do <Router> em App.js, qualquer re-render aqui reexecutaria
  // todo consumidor deste contexto — hoje basicamente só AjusteGrade.js, mas
  // é o tipo de coisa que sai caro silenciosamente se outra tela passar a
  // usar o contexto também.
  const value = useMemo(
    () => ({
      alunosSelecionados,
      setAlunosSelecionados,
      alteracoes,
      setAlteracoes,
      atividadeFiltro,
      setAtividadeFiltro,
      professorFiltro,
      setProfessorFiltro,
      diaFiltro,
      setDiaFiltro,
      horarioFiltro,
      setHorarioFiltro,
      turnoFiltro,
      setTurnoFiltro,
      filtrosColuna,
      setFiltrosColuna,
      areasAtivas,
      setAreasAtivas,
      buscaAluno,
      setBuscaAluno,
      limparFiltros,
    }),
    [
      alunosSelecionados,
      alteracoes,
      atividadeFiltro,
      professorFiltro,
      diaFiltro,
      horarioFiltro,
      turnoFiltro,
      filtrosColuna,
      areasAtivas,
      buscaAluno,
    ],
  );

  return <AjusteGradeContext.Provider value={value}>{children}</AjusteGradeContext.Provider>;
}

export function useAjusteGrade() {
  const ctx = useContext(AjusteGradeContext);
  if (!ctx) throw new Error('useAjusteGrade precisa estar dentro de <AjusteGradeProvider>');
  return ctx;
}
